import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Calendar, GraduationCap, Trophy, Users } from 'lucide-react';
import { Trainers } from './Trainers';
import { fadeInUp } from '../../lib/animations';
import { staggerContainer } from '../../lib/animations';
import { useLanguage } from '../../context/LanguageContext';

export const Services = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    {
      icon: <Users className="w-10 h-10" />,
      title: t.services.training,
      description: t.services.trainingDesc,
      link: '/academy'
    },
    {
      icon: <GraduationCap className="w-10 h-10" />,
      title: t.services.academy,
      description: t.services.academyDesc,
      link: '/academy'
    },
    {
      icon: <Trophy className="w-10 h-10" />,
      title: t.services.tournaments,
      description: t.services.tournamentsDesc,
      link: '/events'
    },
    {
      icon: <Calendar className="w-10 h-10" />,
      title: t.services.camps,
      description: t.services.campsDesc,
      link: '/events'
    }
  ];

  return (
    <section id="services" ref={ref} className="py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <p className="overline mb-4">{t.services.label}</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
            {t.services.title}
          </h2>
          <p className="text-xl text-slate-600">
            {t.services.subtitle}
          </p>
        </motion.div>

        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="minimal-card p-8 rounded-2xl text-center group cursor-pointer"
            >
              <Link to={service.link} className="block" data-testid={`service-link-${service.link.replace('/','')}-${index}`}>
                <div className="w-20 h-20 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-6 text-slate-900 group-hover:bg-slate-900 group-hover:text-white transition-all duration-300">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{service.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Trainers Section
